import { createSelector } from "reselect";
const excludesFalse = (n) => Boolean(n);
function mustSelectEntity(defaultEntity) {
    const isFn = typeof defaultEntity === "function";
    return function selectEntity(selectById) {
        return (state, { id }) => {
            if (isFn) {
                const entity = defaultEntity;
                return selectById(state, { id }) || entity();
            }
            return selectById(state, { id }) || defaultEntity;
        };
    };
}
function tableSelectors(selectTable, empty) {
    const must = empty ? mustSelectEntity(empty) : null;
    const tableAsList = (data) => Object.values(data).filter(excludesFalse);
    const findById = (data, { id }) => data[id];
    const findByIds = (data, { ids }) => ids.map((id) => data[id]).filter(excludesFalse);
    const selectById = (state, { id }) => {
        const data = selectTable(state);
        return findById(data, { id });
    };
    const sbi = must ? must(selectById) : selectById;
    return {
        findById: must ? must(findById) : findById,
        findByIds,
        tableAsList,
        selectTable,
        selectTableAsList: createSelector(selectTable, (data) => tableAsList(data)),
        selectById: sbi,
        selectByIds: createSelector(selectTable, (_, p) => p.ids, (data, ids) => findByIds(data, { ids })),
    };
}
export function createTable({ name, empty, initialState = {}, }) {
    const selectors = tableSelectors((s) => s[name], empty);
    return {
        schema: "table",
        name: name,
        initialState,
        empty: typeof empty === "function" ? empty() : empty,
        add: (entities) => (s) => {
            const state = selectors.selectTable(s);
            Object.keys(entities).forEach((id) => {
                state[id] = entities[id];
            });
        },
        set: (entities) => (s) => {
            // deno-lint-ignore no-explicit-any
            s[name] = entities;
        },
        remove: (ids) => (s) => {
            const state = selectors.selectTable(s);
            ids.forEach((id) => {
                delete state[id];
            });
        },
        patch: (entities) => (s) => {
            const state = selectors.selectTable(s);
            Object.keys(entities).forEach((id) => {
                state[id] = { ...state[id], ...entities[id] };
            });
        },
        merge: (entities) => (s) => {
            const state = selectors.selectTable(s);
            Object.keys(entities).forEach((id) => {
                const entity = entities[id];
                Object.keys(entity).forEach((prop) => {
                    const val = entity[prop];
                    if (Array.isArray(val)) {
                        // deno-lint-ignore no-explicit-any
                        const list = val;
                        // deno-lint-ignore no-explicit-any
                        state[id][prop].push(...list);
                    }
                    else {
                        // deno-lint-ignore no-explicit-any
                        state[id][prop] = entities[id][prop];
                    }
                });
            });
        },
        reset: () => (s) => {
            // deno-lint-ignore no-explicit-any
            s[name] = initialState;
        },
        ...selectors,
    };
}
export function table({ initialState, empty, } = {}) {
    return (name) => createTable({ name, empty, initialState });
}
